/**
 * "Horário" | "Período" do "Agendar" (spec 039, adendo 3) — módulo PURO (sem
 * React, sem alias `@/`), porque a guarda da API
 * (apps/api/src/shared/agenda-period-source.test.ts) o importa: o web não tem
 * executor de testes.
 *
 * - Com que o drawer abre (`initialPlateSchedule`): reagendar mostra o tipo
 *   gravado; vindo da lista "Aguardando data" com a resposta do bot, abre em
 *   "Período" com ela; sem nada, em "Horário", 08:00–09:00, como sempre.
 * - O que ele manda (`plateScheduleBody`): em "Período", o período e a janela
 *   dele (o servidor grava a janela e ignora os horários); em "Horário",
 *   `period: null` e os horários digitados — de período para horário tira o
 *   período.
 * - Quem reagenda (`editsInPlateDrawer`): o "Editar compromisso" de sempre é
 *   só horário e, salvando, apagaria o período; o compromisso de período vai
 *   sempre para o drawer do "Agendar", que mostra o tipo dele.
 */
import {
  botPeriodWindow,
  commitmentPeriodWindow,
  type Commitment,
  type CommitmentPeriod,
} from '@erp/shared';

export type PlateScheduleKind = 'time' | 'period';

/** A escolha do drawer. Os dois lados ficam guardados: trocar e voltar não perde nada. */
export interface PlateSchedule {
  kind: PlateScheduleKind;
  /** o período de "Período" */
  period: CommitmentPeriod;
  /** os horários de "Horário" */
  startTime: string;
  endTime: string;
}

export function initialPlateSchedule(input: {
  /** reagendar: o compromisso gravado */
  initial?: Pick<Commitment, 'period' | 'startTime' | 'endTime'>;
  /** o período que o cliente escolheu no bot (lista "Aguardando data"), ou null */
  botPeriod?: CommitmentPeriod | null;
}): PlateSchedule {
  const { initial, botPeriod = null } = input;
  if (initial) {
    return {
      kind: initial.period ? 'period' : 'time',
      period: initial.period ?? 'manha',
      startTime: initial.startTime,
      endTime: initial.endTime,
    };
  }
  return {
    kind: botPeriod ? 'period' : 'time',
    period: botPeriod ?? 'manha',
    // trocando para "Horário", a janela do período do bot (sem ele, 08:00–09:00)
    ...botPeriodWindow(botPeriod),
  };
}

/** "Período" vale sem horário; "Horário" exige o fim depois do início. */
export function plateScheduleValid(s: PlateSchedule): boolean {
  if (s.kind === 'period') return true;
  return s.startTime !== '' && s.endTime !== '' && s.endTime > s.startTime;
}

/** O pedaço do corpo do compromisso que a escolha decide. */
export function plateScheduleBody(s: PlateSchedule): {
  period: CommitmentPeriod | null;
  startTime: string;
  endTime: string;
} {
  if (s.kind === 'period') return { period: s.period, ...commitmentPeriodWindow(s.period) };
  return { period: null, startTime: s.startTime, endTime: s.endTime };
}

/** O drawer de placa reagenda o que ele mesmo cria: um dia, uma pessoa. */
export function editsAsPlate(c: Pick<Commitment, 'startDate' | 'endDate' | 'assigneeUserId'>): boolean {
  return c.startDate === c.endDate && c.assigneeUserId !== null;
}

/**
 * Quem reagenda pelo drawer do "Agendar": na aba por placa, o que couber nele
 * (`editsAsPlate`); na "Agenda", só o de período — o "Editar compromisso" de
 * lá é só horário, e salvar ali apagaria o período.
 */
export function editsInPlateDrawer(
  c: Pick<Commitment, 'startDate' | 'endDate' | 'assigneeUserId' | 'period'>,
  platesTab: boolean
): boolean {
  return editsAsPlate(c) && (platesTab || c.period !== null);
}
